
import React, { useState, useRef, useEffect } from "react";
import { askAI as sendAIRequest } from "../services/api";

// =========================================================
// HELPERS
// =========================================================

const parseMarkdownTable = (text) => {
  const lines = text.split("\n");
  const tableStart = lines.findIndex(
    (line) => line.includes("|") && line.match(/\|.*\|/)
  );
  if (tableStart === -1 || tableStart + 1 >= lines.length) return null;

  const separatorIndex = lines.slice(tableStart + 1).findIndex((line) =>
    line.trim().match(/^\s*\|?[\s:-]+\|[\s:-|]+$/)
  );
  if (separatorIndex === -1) return null;

  const headerLine = lines[tableStart];
  const headerCells = headerLine
    .split("|")
    .map((cell) => cell.trim())
    .filter((cell) => cell.length > 0);

  const rowLines = lines.slice(tableStart + 2 + separatorIndex);
  const rows = rowLines
    .map((row) => row.split("|").map((cell) => cell.trim()))
    .filter(
      (cells) => cells.length === headerCells.length && cells.some((c) => c.length > 0)
    );

  if (rows.length === 0) return null;

  const prefix = lines.slice(0, tableStart).join("\n").trim();
  return {
    prefix: prefix || null,
    headers: headerCells,
    rows,
  };
};

// =========================================================
// COMPONENT
// =========================================================

function AIQueryPanel() {

  const [question, setQuestion] = useState("");
  const [messages, setMessages] = useState([
    {
      role: "ai",
      text: "Hello! 👋 I'm Shine, your pricing data assistant for Farm2Bag. Ask me about prices, categories, platforms, or trends. I will access the relevant backend collection based on your question.",
    },
  ]);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [dataError, setDataError] = useState("");

  const chatBoxRef = useRef(null);

  // -------------------------------------------------------
  // AUTO SCROLL
  // -------------------------------------------------------

  useEffect(() => {
    if (chatBoxRef.current) {
      chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
    }
  }, [messages, loading]);

  // -------------------------------------------------------
  // ASK AI
  // -------------------------------------------------------

  const parseMarkdownTable = (text) => {
    const lines = text.split("\n");
    const tableStart = lines.findIndex((line) => line.includes("|") && line.match(/\|.*\|/));
    if (tableStart === -1 || tableStart + 1 >= lines.length) return null;

    const separatorIndex = lines.slice(tableStart + 1).findIndex((line) =>
      line.trim().match(/^\s*\|?[\s:-]+\|[\s:-|]+$/)
    );

    if (separatorIndex === -1) return null;

    const sepLine = lines[tableStart + 1 + separatorIndex];
    const headerLine = lines[tableStart];
    const headerCells = headerLine
      .split("|")
      .map((cell) => cell.trim())
      .filter((cell) => cell.length > 0);

    const rowLines = lines.slice(tableStart + 2 + separatorIndex);
    const rows = rowLines
      .map((row) => row.split("|").map((cell) => cell.trim()))
      .filter((cells) => cells.length === headerCells.length && cells.some((c) => c.length > 0));

    if (rows.length === 0) return null;

    const prefix = lines.slice(0, tableStart).join("\n").trim();
    return {
      prefix: prefix || null,
      headers: headerCells,
      rows,
    };
  };

  const askAI = async () => {
    if (!question.trim()) return;

    const userMessage = { role: "user", text: question };
    setMessages((prev) => [...prev, userMessage]);
    setLoading(true);
    setDataError("");

    try {
      const response = await sendAIRequest({ question });
      const aiText = response.data?.answer || response.data?.message || "No response received.";

      setMessages((prev) => [
        ...prev,
        { role: "ai", text: aiText },
      ]);

      setHistory((prev) => [
        ...prev,
        { role: "user", content: question },
        { role: "assistant", content: aiText },
      ].slice(-20));
    } catch (err) {
      console.error(err);
      const errorText =
        err.response?.data?.error ||
        err.message ||
        "An unexpected AI error occurred.";

      setMessages((prev) => [
        ...prev,
        {
          role: "ai",
          text: "I'm sorry, I ran into an issue: " + errorText,
        },
      ]);
      setDataError(errorText);
    }

    setQuestion("");
    setLoading(false);
  };

  // -------------------------------------------------------
  // RENDER
  // -------------------------------------------------------

  return (

    <div className="ai-page">

      {/* CHAT AREA */}

      <div className="ai-chat-container">

        <h2 className="ai-title">
          AI Data Assistant &nbsp;
          <span className="ai-name-badge">Shine</span>
        </h2>

        <div className="ai-upload-bar">
          <span className="ai-upload-status">
            ✅ AI assistant ready. I will access the relevant backend collection based on your question.
          </span>
          {dataError && (
            <span className="ai-upload-error">{dataError}</span>
          )}
        </div>

        <div
          className="ai-chat-box"
          ref={chatBoxRef}
        >

          {messages.length === 0 && (

            <div className="ai-empty">

              Ask questions like:

              <br /><br />

              • Show last 2 days price of all products in F2B and Zepto

              <br />

              • Which category has highest price difference?

              <br />

              • Show cheapest products in Blinkit

            </div>

          )}

          {messages.map((msg, index) => {
            const table =
              msg.role === "ai"
                ? parseMarkdownTable(msg.text)
                : null;

            return (
              <div
                key={index}
                className={
                  msg.role === "user"
                    ? "user-message"
                    : "ai-message"
                }
              >
                <div className="message-label">
                  {msg.role === "user" ? "You" : "Shine"}
                </div>

                {table ? (
                  <>
                    {table.prefix && (
                      <div className="message-text">
                        {table.prefix}
                      </div>
                    )}
                    <div className="message-table-wrapper">
                      <table className="ai-table">
                        <thead>
                          <tr>
                            {table.headers.map((header, idx) => (
                              <th key={idx}>{header}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {table.rows.map((row, rowIndex) => (
                            <tr key={rowIndex}>
                              {row.map((cell, cellIndex) => (
                                <td key={cellIndex}>{cell}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : (
                  <div className="message-text">{msg.text}</div>
                )}
              </div>
            );
          })}

          {loading && (

            <div className="ai-message">

              <div className="message-label">
                Shine
              </div>

              <div className="message-text">
                Thinking...
              </div>

            </div>

          )}

        </div>

      </div>

      {/* INPUT AREA */}

      <div className="ai-input-container">

        <input
          type="text"
          placeholder="Ask Shine about pricing data..."
          value={question}
          onChange={(e) =>
            setQuestion(e.target.value)
          }
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              askAI();
            }
          }}
        />

        <button onClick={askAI}>
          Ask AI
        </button>

      </div>

    </div>
  );
}

export default AIQueryPanel;